/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { IOrderLevelCommentData } from './order-level-comment.data';
import { IOrderLevelCommentProps } from './order-level-comment.props.autogenerated';
import { Button, IModuleProps, INodeProps, Modal, ModalFooter, ModalHeader } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as Msdyn365 from '@msdyn365-commerce/core';

export interface IOrderLevelCommentViewProps extends IOrderLevelCommentProps<IOrderLevelCommentData> {
    staticDisplay: INodeProps;
    orderLevelCommentModuleProps: IModuleProps;
    modal: React.ReactNode;
}

interface IOrderLevelCommentExtendedProps extends IOrderLevelCommentProps<IOrderLevelCommentData> {
    comment: string;
}

interface IOrderLevelCommentState {
    comment: string;
    isCommentAreaActive: boolean;
}

/**
 *
 * OrderLevelComment component
 * @extends {React.PureComponent<IOrderLevelCommentProps<IOrderLevelCommentData>>}
 */
class OrderLevelComment extends React.PureComponent<IOrderLevelCommentExtendedProps, IOrderLevelCommentState> {
    private _className = 'gar-order-comment';
    private _maxCommentLength = 60;
    constructor(props: IOrderLevelCommentExtendedProps) {
        super(props);
        this.state = {
            comment: props.data.cart.result?.cart.Comment || '',
            isCommentAreaActive: false
        };
    }

    /**
     * Saves order comment
     */

    private _updateOrderLevelComment = async (): Promise<void> => {
        const cartState = this.props.data.cart.result;
        if (!cartState?.cart) {
            return;
        }
        const dummyCart = { ...cartState.cart };
        dummyCart.Comment = this.state.comment;
        await cartState.updateCart({ newCartObject: dummyCart });
        this.setState({ isCommentAreaActive: false });
    };

    /**
     * Updates the comment in state
     * @param event new comment from event
     */
    private _updateCommentState = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
        this.setState({ comment: event.target.value });
    };
    /**
     * Resets Cart Comment
     */
    private _resetCommentToProps = (): void => {
        this.setState({ comment: this.props.comment, isCommentAreaActive: false });
    };

    /**
     * Toggles the isCommentAreaActive state property
     */
    private _toggleCommentAreaActive = () =>
        this.setState(prevState => {
            return {
                isCommentAreaActive: !prevState.isCommentAreaActive
            };
        });

    /**
     * Renders the static comment display for when the isCommentAreaActive state param is false
     * @return React.ReactNode
     */
    private _renderStaticCommentDisplay = (): React.ReactNode => {
        const {
            resources: { commentLinkText, editLinkText },
            config: { disclaimer }
        } = this.props;
        const currentComment = this.props.data.cart.result?.cart.Comment;
        if (!currentComment) {
            return (
                <>
                    {this._renderCommentLink(commentLinkText)}
                    <Msdyn365.RichTextComponent text={disclaimer} className={`${this._className}__comment-disclaimer`} />
                </>
            );
        } else {
            return (
                <>
                    <p className={`${this._className}__comment-preview`}>{this._getCommentPreview()}</p>
                    {this._renderCommentLink(editLinkText)}
                    {disclaimer ? (
                        <Msdyn365.RichTextComponent text={disclaimer} className={`${this._className}__comment-disclaimer`} />
                    ) : null}
                </>
            );
        }
    };

    private _getCommentPreview = (): string => {
        const comment = this.props.data.cart.result?.cart.Comment || '';
        if (comment.length > 50) {
            return `${comment.substring(0, 50)}...`;
        } else {
            return comment;
        }
    };

    /**
     * Renders a comment link which toggles the active area state
     */
    private _renderCommentLink = (linkText: string): React.ReactNode => (
        <a href={'#'} onClick={this._toggleCommentAreaActive} className={`${this._className}__link`}>
            {linkText}
        </a>
    );

    /**
     * Renders a button to save comments to the cartlines
     */
    private _renderSaveButton = (): React.ReactNode => {
        const {
            resources: { saveButtonText }
        } = this.props;
        return (
            <Button onClick={this._updateOrderLevelComment} className={`${this._className}__button ${this._className}__button--save`}>
                {saveButtonText}
            </Button>
        );
    };

    /**
     * Renders a button to cancel the comment entry and set the state to default
     */
    private _renderCancelButton = (): React.ReactNode => {
        const {
            resources: { cancelButtonText }
        } = this.props;
        return (
            <Button onClick={this._resetCommentToProps} className={`${this._className}__button ${this._className}__button--cancel`}>
                {cancelButtonText}
            </Button>
        );
    };

    /**
     * Renders the text area for inputting comments
     */
    private _renderTextArea = (): React.ReactNode => {
        const {
            resources: { textAreaLabel, charactersRemaining }
        } = this.props;
        const { comment } = this.state;
        return (
            <div className={`${this._className}__textarea-wrapper`}>
                <label htmlFor={this._getFieldId()} className={`${this._className}__textarea-label`}>
                    {textAreaLabel}
                </label>
                <textarea
                    id={this._getFieldId()}
                    value={comment}
                    onChange={this._updateCommentState}
                    maxLength={this._maxCommentLength}
                    className={`${this._className}__textarea`}
                />
                <p className={`${this._className}__char-limit`}>
                    {`${this._maxCommentLength - (comment && comment.length ? comment.length : 0)} ${charactersRemaining}`}
                </p>
            </div>
        );
    };

    /**
     * Renders the button area
     */
    private _renderButtonArea = (): React.ReactNode => (
        <div className={`${this._className}__button-area`}>
            {this._renderSaveButton()}
            {this._renderCancelButton()}
        </div>
    );

    /**
     * Renders the complete modal
     */
    private _renderModal = () => {
        return (
            <Modal isOpen={this.state.isCommentAreaActive} toggle={this._toggleCommentAreaActive}>
                <ModalHeader className={`${this._className}__modal-header`}>{this._getModalTitle()}</ModalHeader>
                {this._renderTextArea()}
                <ModalFooter>{this._renderButtonArea()}</ModalFooter>
            </Modal>
        );
    };

    /**
     * Gets ID for the text field
     */
    private _getFieldId = (): string => `orderLevelCommentInput`;

    /**
     * Gets the title for the comment modal
     */
    private _getModalTitle = (): string => {
        const {
            resources: { commentModalTitle }
        } = this.props;

        return commentModalTitle;
    };

    public render(): JSX.Element | null {
        const {
            config: { className }
        } = this.props;
        const viewProps: IOrderLevelCommentViewProps = {
            ...this.props,
            staticDisplay: {
                tag: 'div',
                className: `${this._className}__static-display`,
                children: this._renderStaticCommentDisplay()
            },
            orderLevelCommentModuleProps: {
                className: classnames(className, this._className),
                tag: 'div',
                moduleProps: { ...this.props }
            },
            modal: this._renderModal()
        };

        return this.props.renderView(viewProps);
    }
}

export default OrderLevelComment;
